// ════════════════════════════════════════════════════════════════════════
//  Code-Kärtchen als PDF
//
//  Client-seitig erzeugt – es gibt keinen Grund, Codes für einen Ausdruck
//  über einen Server zu schicken.
//
//  Auf jedem Kärtchen ist eine Linie für den Namen des Kindes. Genau dort
//  entsteht die Zuordnung Code ↔ Kind, mit einem Stift, auf Papier, bei der
//  Lehrkraft. Diese Linie ist kein Layout-Detail, sondern das Verfahren.
// ════════════════════════════════════════════════════════════════════════

import { PDFDocument, StandardFonts } from "pdf-lib";
import { embedQr } from "../../../shared/lib/qr.js";
import { embedOtto } from "../../../shared/lib/otto.js";
import {
  A4,
  RAND,
  farben,
  fileNamePart,
  sizeToFit,
  toWinAnsi,
} from "../../../shared/lib/pdf.js";

const COLUMNS = 2;
const ROWS = 4;
export const CARDS_PER_PAGE = COLUMNS * ROWS;

/** Kantenlänge des QR-Codes: gut 22 mm, samt Ruhezone. */
const QR_SIZE = 62;

/** Abstand zwischen dem Text links und dem QR-Code rechts. */
const QR_GAP = 10;

/**
 * Die Adresse der Lern-App, oder null.
 *
 * Null heißt: das Kärtchen sieht aus wie vorher. Das ist der bessere Ausgang als
 * ein QR auf "http://localhost:5173" – der sähe richtig aus und führte in einer
 * Schule ins Nichts.
 */
export function appAddress(url: string | undefined): string | null {
  const text = (url ?? "").trim();
  if (text === "") return null;
  try {
    const parsed = new URL(text);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? text : null;
  } catch {
    return null;
  }
}

/** Zum Abtippen, nicht zum Anklicken: ohne Schema und ohne Schrägstrich am Ende. */
export function readableAddress(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

export type CardsInput = {
  readonly schoolClass: string;
  readonly schoolYear: string;
  readonly codes: readonly string[];
  /**
   * Adresse der Lern-App für den QR-Code. Fehlt sie, wird keiner gedruckt.
   *
   * Der QR trägt AUSSCHLIESSLICH diese Adresse, nie den Übungscode. Ein
   * `?code=esel-lila-4711` bliebe in der Verlaufsliste von Safari, in der
   * Adressvervollständigung und in der Historie der Kamera-App des Koffer-iPads
   * stehen, das morgen ein anderes Kind benutzt – genau das Residuum, das die
   * Lern-App mit dem sessionStorage vermeidet. Und ein abfotografiertes Kärtchen
   * wäre damit eine Anmeldung statt eines Zettels.
   */
  readonly appUrl?: string | undefined;
};

export async function createCardsPdf(input: CardsInput): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  doc.setTitle(`Übungscodes ${input.schoolClass} ${input.schoolYear}`);
  doc.setCreator("Otto tippt los");

  const normal = await doc.embedFont(StandardFonts.Helvetica);
  const fett = await doc.embedFont(StandardFonts.HelveticaBold);

  const cellWidth = (A4.breite - 2 * RAND) / COLUMNS;
  const cellHeight = (A4.hoehe - 2 * RAND) / ROWS;
  const { grey, ink, quiet } = farben;

  // Einmal gezeichnet, auf jedem Kärtchen dieselbe Form: derselbe QR steht auf
  // allen Kärtchen der Klasse. Modul für Modul in jede der 25 Zellen geschrieben
  // wäre das Blatt ein Megabyte schwer.
  // Otto einmal zeichnen und als Form einbetten - wie den QR-Code, und aus
  // demselben Grund: acht Kärtchen je Seite, 25 je Klassensatz.
  const otto = await embedOtto(doc, 34);

  const address = appAddress(input.appUrl);
  const qr =
    address === null
      ? null
      : await embedQr(doc, { text: address, size: QR_SIZE, color: ink });

  // Auch ohne Codes eine Seite: ein leeres PDF sieht wie ein Fehler aus.
  const pageCount = Math.max(1, Math.ceil(input.codes.length / CARDS_PER_PAGE));

  for (let pageNo = 0; pageNo < pageCount; pageNo++) {
    const side = doc.addPage([A4.breite, A4.hoehe]);

    for (let slot = 0; slot < CARDS_PER_PAGE; slot++) {
      const index = pageNo * CARDS_PER_PAGE + slot;
      if (index >= input.codes.length) break;

      const column = slot % COLUMNS;
      const row = Math.floor(slot / COLUMNS);
      const x = RAND + column * cellWidth;
      const y = A4.hoehe - RAND - (row + 1) * cellHeight;

      // Schnittlinie, damit man die Kärtchen auseinanderschneiden kann.
      side.drawRectangle({
        x: x + 6,
        y: y + 6,
        width: cellWidth - 12,
        height: cellHeight - 12,
        borderColor: grey,
        borderWidth: 0.75,
        borderDashArray: [3, 3],
      });

      const insideX = x + 24;
      const top = y + cellHeight - 30;

      side.drawText("Otto tippt los", {
        x: insideX,
        y: top,
        size: 9,
        font: normal,
        color: quiet,
      });

      side.drawText(toWinAnsi(`${input.schoolClass} · ${input.schoolYear}`), {
        x: insideX,
        y: top - 14,
        size: 9,
        font: normal,
        color: quiet,
      });

      side.drawText("Mein Übungscode", {
        x: insideX,
        y: top - 46,
        size: 10,
        font: normal,
        color: quiet,
      });

      // Rechts steht der QR-Code, links darf der Text nur bis dorthin laufen.
      const textWidth =
        (qr === null ? x + cellWidth - 24 : x + cellWidth - 24 - QR_SIZE - QR_GAP) -
        insideX;

      // Der Code groß: Drittklässler tippen ihn ab.
      const code = toWinAnsi(input.codes[index]!);
      side.drawText(code, {
        x: insideX,
        y: top - 70,
        size: sizeToFit(fett, code, textWidth, 17, 11),
        font: fett,
        color: ink,
      });

      if (qr !== null && address !== null) {
        // Oben rechts, neben Kopfzeile und Code – unter der Namenslinie wäre kein
        // Platz für 22 mm, und angeschnitten wird ein QR nicht mehr gelesen.
        side.drawPage(qr, {
          x: x + cellWidth - 24 - QR_SIZE,
          y: top - 80,
          width: QR_SIZE,
          height: QR_SIZE,
        });

        // Die Adresse zusätzlich in Klartext: ein Scanner ist nicht immer zur
        // Hand, und ein zerknicktes Kärtchen muss man abtippen können.
        const line = toWinAnsi(`Zum Üben: ${readableAddress(address)}`);
        side.drawText(line, {
          x: insideX,
          y: top - 92,
          size: sizeToFit(normal, line, textWidth, 8, 5.5),
          font: normal,
          color: quiet,
        });
      }

      // Otto unten links: der freie Platz unter der Namenslinie. Er grüßt das
      // Kind, das sein Kärtchen bekommt - mehr soll er hier nicht.
      side.drawPage(otto, { x: insideX, y: y + 14, width: 34, height: 34 });

      // Namenslinie – hier entsteht die analoge Zuordnung.
      side.drawText("Name:", {
        x: insideX,
        y: top - 108,
        size: 10,
        font: normal,
        color: quiet,
      });
      side.drawLine({
        start: { x: insideX + 42, y: top - 111 },
        end: { x: x + cellWidth - 24, y: top - 111 },
        thickness: 0.75,
        color: grey,
      });
    }
  }

  return doc.save();
}

/** Dateiname mit Klasse und Schuljahr – im Downloadordner sonst nicht zu finden. */
export function cardsFileName(schoolClass: string, schoolYear: string): string {
  return `uebungscodes-${fileNamePart(schoolClass)}-${fileNamePart(schoolYear)}.pdf`;
}
